import * as React from "react";

import { cx } from "./controlStyles";

// Badge: a small 20px pill for a status word, a tag or a count. 12px
// medium sans, sentence case — never mono caps. Tones map to the theme's
// semantic colours; the soft tint is 10%, which keeps tone-coloured text
// at >= 4.5:1 on panels and dialogs in every theme. Colour is never the
// only cue: pair a tone with a word (and a dot or icon where it helps).
//
//   appearance "soft" (default) tinted fill, "outline" ring only, "plain" text only.

export type BadgeTone =
  | "default"
  | "muted"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral"
  // live work (a running status): the accent unless the theme sets its own
  | "live";

export type BadgeAppearance = "soft" | "outline" | "plain";

const TEXT: Record<BadgeTone, string> = {
  default: "text-fg-muted",
  neutral: "text-fg-muted",
  muted: "text-fg-subtle",
  accent: "text-accent-fg",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
  live: "text-live",
};

const SOFT: Record<BadgeTone, string> = {
  default: "bg-raised",
  neutral: "bg-raised",
  muted: "bg-raised",
  // The selected fill: in Midnight it sits well above bg-raised (12% vs
  // 5.5% white), so accent never reads as neutral there either.
  accent: "bg-selected",
  success: "bg-success/10",
  warning: "bg-warning/10",
  danger: "bg-danger/10",
  info: "bg-info/10",
  live: "bg-[color:var(--c-live-tint)]",
};

const OUTLINE: Record<BadgeTone, string> = {
  default: "shadow-[inset_0_0_0_1px_var(--c-border-visible)]",
  neutral: "shadow-[inset_0_0_0_1px_var(--c-border-visible)]",
  muted: "shadow-[inset_0_0_0_1px_var(--c-border-subtle)]",
  accent: "shadow-[inset_0_0_0_1px_var(--c-glass-active-accent-border)]",
  success: "shadow-[inset_0_0_0_1px_rgb(var(--tw-success)/0.4)]",
  warning: "shadow-[inset_0_0_0_1px_rgb(var(--tw-warning)/0.4)]",
  danger: "shadow-[inset_0_0_0_1px_rgb(var(--tw-danger)/0.4)]",
  info: "shadow-[inset_0_0_0_1px_rgb(var(--tw-info)/0.4)]",
  live: "shadow-[inset_0_0_0_1px_var(--c-live-border)]",
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Kept name from the first Badge; same values as `tone`. */
  variant?: BadgeTone | null;
  tone?: BadgeTone;
  appearance?: BadgeAppearance;
  /** A round dot in the tone colour before the label. */
  dot?: boolean;
  icon?: React.ReactNode;
}

export function badgeClasses(tone: BadgeTone = "default", appearance: BadgeAppearance = "soft") {
  return cx(
    "inline-flex h-[20px] max-w-full shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill text-xs font-medium",
    appearance === "plain" ? "px-0" : "px-2",
    TEXT[tone],
    appearance === "soft" && SOFT[tone],
    appearance === "outline" && OUTLINE[tone],
  );
}

export function Badge({
  className,
  variant,
  tone,
  appearance = "soft",
  dot,
  icon,
  children,
  ...props
}: BadgeProps) {
  const t = tone ?? variant ?? "default";
  return (
    <span className={cx(badgeClasses(t, appearance), className)} {...props}>
      {dot && <span aria-hidden className="h-[6px] w-[6px] shrink-0 rounded-full bg-current" />}
      {icon && (
        <span aria-hidden className="flex shrink-0 items-center [&>svg]:h-3 [&>svg]:w-3">
          {icon}
        </span>
      )}
      {children !== undefined && children !== null && <span className="truncate">{children}</span>}
    </span>
  );
}
